/**
 * SeismosComparisonChart.tsx  —  v2
 *
 * Three-view analysis panel for Seismos intervention effectiveness.
 *
 * View 1 — Stage Comparison
 *   Sub-toggle: All Stages | ±3 Neighbors (controls toe/heel pressure bias, SPE-217824)
 *   All Stages: responded vs baseline scatter + trend lines
 *   ±3 Neighbors: each responded stage connected to its specific neighbor mean
 *   by a Δ connector line — mirrors the Excel Summary tab methodology exactly
 *
 * View 2 — Intra-Stage Event Study
 *   X-axis = event position relative to intervention (−4 … 0 … +4)
 *   Y-axis = perf efficiency per event, all responded stages overlaid
 *   Group mean lines (amber pre, purple post) with Δ stat
 *   Tests whether eff improved WITHIN the stage after the intervention
 *   Lazy-fetches per-stage event data on first open
 *
 * View 3 — Multi-Metric Δ Table
 *   UI, Eff, EHD, PerfFrict, PipeFrict — responded mean vs all-baseline AND ±3 neighbor mean
 *   Δ and Δ% — mirrors the Excel Summary tab columns exactly
 *
 * Props added vs v1:
 *   seismosInterventions  — full records with seismos_metrics_id for event matching
 *   apiBase / token       — for lazy-fetching stage events in View 2
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

// ─── Brand ────────────────────────────────────────────────────────────────────
const BG        = '#181716';
const BORDER    = '#38352f';
const TEXT_PRI  = '#EDE8DF';
const TEXT_SEC  = '#8a8480';
const TEXT_DIM  = '#504d48';
const GOLD      = '#F9C430';
const CARD_BG   = '#242220';
const C_SEISMOS = '#a78bfa';
const C_BASE    = '#8a8480';
const C_PRE     = '#f0a050';   // amber — pre-intervention events
const C_POST    = '#a78bfa';   // purple — post-intervention events

// ─── Types ────────────────────────────────────────────────────────────────────
export interface StageMin {
  stage_id:         number;
  stage_number:     number;
  well_name:        string;
  avg_eff:          number | null;
  avg_perf_frict:   number | null;
  avg_pipe_frict:   number | null;
  avg_ehd:          number | null;
  uniformity_index: number | null;
}

export interface SeismosInterventionRecord {
  id:                 number;
  stage_id:           number;
  seismos_metrics_id: number | null;
  intervention_type:  string;
  eff:                number | null;
  stage_number?:      number;
  well_name?:         string;
}

interface StageEvent {
  id:            number;
  event_time_ms: number;
  eff:           number | null;
  ehd:           number | null;
  perf_frict:    number | null;
  pipe_frict:    number | null;
}

interface Props {
  stages:               StageMin[];
  respondedStageIds:    Set<number>;
  showWell:             boolean;
  seismosInterventions: SeismosInterventionRecord[];
  apiBase:              string;
  token:                string;
  level:                'well' | 'pad';
  entityId:             number;
}

type ViewKey     = 'compare' | 'intra' | 'metrics';
type CompareMode = 'all' | 'neighbor';

// ─── Shared helpers ───────────────────────────────────────────────────────────
function linearRegression(pts: { x: number; y: number }[]) {
  const n = pts.length; if (n < 2) return null;
  const sx=pts.reduce((a,p)=>a+p.x,0), sy=pts.reduce((a,p)=>a+p.y,0);
  const sxy=pts.reduce((a,p)=>a+p.x*p.y,0), sx2=pts.reduce((a,p)=>a+p.x*p.x,0);
  const d=n*sx2-sx*sx; if(Math.abs(d)<1e-10) return null;
  return { slope:(n*sxy-sx*sy)/d, intercept:(sy-sx*sy/n)/n };
}
function avg(vals: (number|null)[]): number|null {
  const ns=vals.filter((v):v is number=>v!=null);
  return ns.length ? ns.reduce((a,b)=>a+b)/ns.length : null;
}
const fmt     = (v:number|null,dec=1,unit='') => v!=null?`${v.toFixed(dec)}${unit}`:'—';
const fmtDelta= (v:number|null,dec=1,unit='') => v==null?'—':`${v>0?'+':''}${v.toFixed(dec)}${unit}`;
const dColor  = (v:number|null) => v==null?TEXT_SEC:v>0?'#7ecb8f':'#e05c5c';

// Eff from the stage scorecard endpoint comes back as 0-1 decimal.
// Multiply by 100 if the value is clearly in that range.
const toPercent = (v:number|null): number|null => v==null ? null : (v<=1.5 ? v*100 : v);

// Extract the most distinctive part of a well name.
// "Ferguson-Mcknight H05 Axis 1A" → "H05"
// "Ferguson-Mcknight H07 Axis 1A" → "H07"
// Falls back to last word if no H-number found.
const shortWell = (name:string): string => {
  const m = name.match(/\b(H\d+)\b/);
  if (m) return m[1];
  return name.split(' ').pop() ?? name;
};

// ─── ±3 Neighbor algorithm ────────────────────────────────────────────────────
interface NbResult { mean_eff:number|null; mean_ui:number|null; mean_ehd:number|null; mean_pf:number|null; mean_ppf:number|null; count:number; }

function getNeighbors(stage:StageMin, all:StageMin[], respondedIds:Set<number>, win=3): NbResult {
  const well = all.filter(s=>s.well_name===stage.well_name).sort((a,b)=>a.stage_number-b.stage_number);
  const idx  = well.findIndex(s=>s.stage_id===stage.stage_id);
  if (idx===-1) return {mean_eff:null,mean_ui:null,mean_ehd:null,mean_pf:null,mean_ppf:null,count:0};
  const found:StageMin[]=[]; let off=1;
  while(found.length<win*2 && off<=well.length) {
    const b=idx-off, f=idx+off;
    if(b>=0 && !respondedIds.has(well[b].stage_id)) found.push(well[b]);
    if(f<well.length && !respondedIds.has(well[f].stage_id)) found.push(well[f]);
    off++;
  }
  const ns=found.slice(0,win*2);
  return {
    mean_eff:avg(ns.map(s=>s.avg_eff)), mean_ui:avg(ns.map(s=>s.uniformity_index)),
    mean_ehd:avg(ns.map(s=>s.avg_ehd)), mean_pf:avg(ns.map(s=>s.avg_perf_frict)),
    mean_ppf:avg(ns.map(s=>s.avg_pipe_frict)), count:ns.length,
  };
}

// ─── UI atoms ─────────────────────────────────────────────────────────────────
const StatCard:React.FC<{label:string;value:string;color:string;sub?:string}> = ({label,value,color,sub}) => (
  <div style={{background:CARD_BG,border:`1px solid ${BORDER}`,borderRadius:6,padding:'8px 14px',minWidth:90,textAlign:'center',flex:'0 0 auto'}}>
    <div style={{fontSize:9,color:TEXT_DIM,letterSpacing:'0.06em',textTransform:'uppercase',marginBottom:3}}>{label}</div>
    <div style={{fontSize:15,fontWeight:700,color,lineHeight:1.1}}>{value}</div>
    {sub&&<div style={{fontSize:9,color:TEXT_DIM,marginTop:2}}>{sub}</div>}
  </div>
);
const Tab:React.FC<{label:string;active:boolean;onClick:()=>void;color?:string}> = ({label,active,onClick,color=GOLD}) => (
  <button onClick={onClick} style={{
    background:active?`${color}22`:'transparent', border:`1px solid ${active?color:BORDER}`,
    color:active?color:TEXT_SEC, borderRadius:4, padding:'4px 14px',
    fontSize:10, fontWeight:700, cursor:'pointer', letterSpacing:'0.05em', fontFamily:'inherit',
  }}>{label}</button>
);

// ══════════════════════════════════════════════════════════════════════════════
// VIEW 1 — Stage Comparison (All Stages OR ±3 Neighbors)
// ══════════════════════════════════════════════════════════════════════════════
const ViewCompare:React.FC<{stages:StageMin[];respondedStageIds:Set<number>;showWell:boolean;mode:CompareMode}> = ({stages,respondedStageIds,showWell,mode}) => {
  const result = useMemo(() => {
    const indexed = stages.map((s,i)=>({...s,seqIndex:i+1}));
    const responded = indexed.filter(s=>respondedStageIds.has(s.stage_id));
    const baseline  = indexed.filter(s=>!respondedStageIds.has(s.stage_id));
    const seqMin=1, seqMax=indexed.length;

    if (mode==='all') {
      const rPts=responded.filter(s=>s.avg_eff!=null).map(s=>({x:s.seqIndex,y:s.avg_eff!}));
      const bPts=baseline.filter(s=>s.avg_eff!=null).map(s=>({x:s.seqIndex,y:s.avg_eff!}));
      const rR=linearRegression(rPts), bR=linearRegression(bPts);
      const rT=rR?[[seqMin,rR.intercept+rR.slope*seqMin],[seqMax,rR.intercept+rR.slope*seqMax]]:null;
      const bT=bR?[[seqMin,bR.intercept+bR.slope*seqMin],[seqMax,bR.intercept+bR.slope*seqMax]]:null;
      const sub=[rT?`Responded trend: ${fmtDelta(rT[1][1]-rT[0][1],1,'%')}`:'',bT?`Baseline trend: ${fmtDelta(bT[1][1]-bT[0][1],1,'%')}`:'' ].filter(Boolean).join('   ·   ');

      const opts:Highcharts.Options={
        credits:{enabled:false},title:{text:undefined},
        subtitle:{text:sub,style:{color:TEXT_SEC,fontSize:'10px'}},
        chart:{backgroundColor:BG,style:{fontFamily:"'DM Mono',monospace"},animation:false,height:280},
        legend:{itemStyle:{color:TEXT_SEC,fontSize:'11px',fontWeight:'normal'},itemHoverStyle:{color:TEXT_PRI},backgroundColor:'transparent'},
        xAxis:{min:seqMin,max:seqMax,lineColor:BORDER,tickColor:BORDER,gridLineWidth:0,labels:{style:{color:TEXT_SEC,fontSize:'10px'}},
          title:{text:showWell?'Stage Sequence (all wells)':'Stage Number',style:{color:TEXT_SEC,fontSize:'11px'}},
          plotBands:responded.map(s=>({from:s.seqIndex-0.5,to:s.seqIndex+0.5,color:`${C_SEISMOS}14`,zIndex:0}))},
        yAxis:{title:{text:'Avg Perf Eff (%)',style:{color:TEXT_SEC,fontSize:'11px'}},labels:{format:'{value}%',style:{color:TEXT_SEC,fontSize:'10px'}},min:0,max:105,gridLineColor:BORDER,gridLineDashStyle:'Dot',lineWidth:0},
        tooltip:{backgroundColor:'#1e1c1b',borderColor:BORDER,style:{color:TEXT_PRI,fontSize:'12px'},useHTML:true,
          formatter:function(){const pt=this.point as any;const tag=respondedStageIds.has(pt.stage_id)?`<span style="color:${C_SEISMOS};font-weight:700">● Responded</span>`:`<span style="color:${C_BASE}">● Baseline</span>`;let h=`${tag}<br/>`;if(showWell&&pt.well_name)h+=`<span style="color:${GOLD}">${pt.well_name}</span> · `;h+=`S${pt.stage_number}<br/><span style="color:${TEXT_SEC}">Avg Eff: </span><b>${pt.y!=null?(pt.y as number).toFixed(1)+'%':'—'}</b>`;if(pt.uniformity_index!=null)h+=`<br/><span style="color:${TEXT_SEC}">UI: </span><b>${(pt.uniformity_index as number).toFixed(3)}</b>`;return h;}},
        series:[
          {type:'scatter',name:'Seismos Responded',color:C_SEISMOS,marker:{radius:5,symbol:'circle',fillColor:C_SEISMOS},data:responded.filter(s=>s.avg_eff!=null).map(s=>({x:s.seqIndex,y:s.avg_eff!,stage_id:s.stage_id,stage_number:s.stage_number,well_name:s.well_name,uniformity_index:s.uniformity_index})),zIndex:4},
          {type:'scatter',name:'No Intervention',color:C_BASE,marker:{radius:3,symbol:'circle',fillColor:C_BASE},data:baseline.filter(s=>s.avg_eff!=null).map(s=>({x:s.seqIndex,y:s.avg_eff!,stage_id:s.stage_id,stage_number:s.stage_number,well_name:s.well_name,uniformity_index:s.uniformity_index})),zIndex:3},
          ...(rT?[{type:'line' as const,name:'Responded Trend',color:C_SEISMOS,dashStyle:'ShortDash' as const,lineWidth:1.5,marker:{enabled:false},data:rT,enableMouseTracking:false,zIndex:2}]:[]),
          ...(bT?[{type:'line' as const,name:'Baseline Trend',color:C_BASE,dashStyle:'ShortDash' as const,lineWidth:1.5,marker:{enabled:false},data:bT,enableMouseTracking:false,zIndex:1}]:[]),
        ] as Highcharts.SeriesOptionsType[],
      };
      const delta=avg(responded.map(s=>s.avg_eff))!=null&&avg(baseline.map(s=>s.avg_eff))!=null?avg(responded.map(s=>s.avg_eff))!-avg(baseline.map(s=>s.avg_eff))!:null;
      return {opts,responded,baseline,rAvgEff:avg(responded.map(s=>s.avg_eff)),bAvgEff:avg(baseline.map(s=>s.avg_eff)),rAvgUI:avg(responded.map(s=>s.uniformity_index)),bAvgUI:avg(baseline.map(s=>s.uniformity_index)),delta,pctPos:null as number|null,neighborMode:false};
    }

    // ±3 Neighbor mode
    const nbData=responded.map(s=>{const nb=getNeighbors(s,indexed,respondedStageIds,3);return{stage:s,nb,dEff:s.avg_eff!=null&&nb.mean_eff!=null?s.avg_eff-nb.mean_eff:null};});
    const deltas=nbData.map(d=>d.dEff).filter((v):v is number=>v!=null);
    const avgDelta=avg(deltas);
    const pctPos=deltas.length?Math.round(deltas.filter(v=>v>0).length/deltas.length*100):null;

    const connectors:Highcharts.SeriesOptionsType[]=nbData.filter(d=>d.stage.avg_eff!=null&&d.nb.mean_eff!=null).map(d=>({
      type:'line' as const,showInLegend:false,enableMouseTracking:false,
      color:d.dEff!=null&&d.dEff>0?'#7ecb8f44':'#e05c5c44',lineWidth:1.5,dashStyle:'Dash' as const,
      marker:{enabled:false},data:[[d.stage.seqIndex,d.stage.avg_eff!],[d.stage.seqIndex,d.nb.mean_eff!]],zIndex:1,
    }));

    const opts:Highcharts.Options={
      credits:{enabled:false},title:{text:undefined},
      subtitle:{text:`±3 neighbor-controlled · Avg Δ Eff: ${fmtDelta(avgDelta,1,'%')} · ${pctPos!=null?`${pctPos}% outperformed neighbors`:''}`,style:{color:TEXT_SEC,fontSize:'10px'}},
      chart:{backgroundColor:BG,style:{fontFamily:"'DM Mono',monospace"},animation:false,height:280},
      legend:{itemStyle:{color:TEXT_SEC,fontSize:'11px',fontWeight:'normal'},itemHoverStyle:{color:TEXT_PRI},backgroundColor:'transparent'},
      xAxis:{min:seqMin,max:seqMax,lineColor:BORDER,tickColor:BORDER,gridLineWidth:0,labels:{style:{color:TEXT_SEC,fontSize:'10px'}},title:{text:showWell?'Stage Sequence (all wells)':'Stage Number',style:{color:TEXT_SEC,fontSize:'11px'}}},
      yAxis:{title:{text:'Avg Perf Eff (%)',style:{color:TEXT_SEC,fontSize:'11px'}},labels:{format:'{value}%',style:{color:TEXT_SEC,fontSize:'10px'}},min:0,max:105,gridLineColor:BORDER,gridLineDashStyle:'Dot',lineWidth:0},
      tooltip:{backgroundColor:'#1e1c1b',borderColor:BORDER,style:{color:TEXT_PRI,fontSize:'12px'},useHTML:true,
        formatter:function(){const pt=this.point as any;if(this.series.name==='Neighbor Mean (±3)')return `<span style="color:${C_BASE}">✕ Neighbor Mean</span><br/>${showWell&&pt.well_name?`<span style="color:${GOLD}">${pt.well_name}</span> · `:''}S${pt.stage_number}<br/>Eff: <b>${(pt.y as number).toFixed(1)}%</b>`;const dc=pt.dEff!=null?` · Δ: <b style="color:${pt.dEff>0?'#7ecb8f':'#e05c5c'}">${fmtDelta(pt.dEff,1,'%')}</b>`:'';return `<span style="color:${C_SEISMOS};font-weight:700">● Responded</span>${dc}<br/>${showWell&&pt.well_name?`<span style="color:${GOLD}">${pt.well_name}</span> · `:''}S${pt.stage_number}<br/>Eff: <b>${(pt.y as number).toFixed(1)}%</b>`;}},
      series:[
        ...connectors,
        {type:'scatter',name:'No Intervention',color:C_BASE,marker:{radius:2,symbol:'circle',fillColor:`${C_BASE}66`},data:baseline.filter(s=>s.avg_eff!=null).map(s=>({x:s.seqIndex,y:s.avg_eff!})),zIndex:1},
        {type:'scatter',name:'Neighbor Mean (±3)',color:C_BASE,marker:{symbol:'cross',radius:4,lineWidth:2,lineColor:C_BASE},data:nbData.filter(d=>d.nb.mean_eff!=null).map(d=>({x:d.stage.seqIndex,y:d.nb.mean_eff!,stage_number:d.stage.stage_number,well_name:d.stage.well_name})),zIndex:3},
        {type:'scatter',name:'Responded Stage',marker:{radius:6,symbol:'circle'},data:nbData.filter(d=>d.stage.avg_eff!=null).map(d=>({x:d.stage.seqIndex,y:d.stage.avg_eff!,stage_id:d.stage.stage_id,stage_number:d.stage.stage_number,well_name:d.stage.well_name,dEff:d.dEff,marker:{fillColor:d.dEff==null?C_SEISMOS:d.dEff>0?'#7ecb8f':'#e05c5c',lineWidth:0}})),zIndex:4},
      ] as Highcharts.SeriesOptionsType[],
    };
    return {opts,responded,baseline,rAvgEff:avg(responded.map(s=>s.avg_eff)),bAvgEff:avg(nbData.map(d=>d.nb.mean_eff)),rAvgUI:avg(responded.map(s=>s.uniformity_index)),bAvgUI:avg(nbData.map(d=>d.nb.mean_ui)),delta:avgDelta,pctPos,neighborMode:true};
  },[stages,respondedStageIds,showWell,mode]);

  const {opts,responded,baseline,rAvgEff,bAvgEff,rAvgUI,bAvgUI,delta,pctPos,neighborMode}=result;
  return (
    <div>
      <div style={{display:'flex',flexWrap:'wrap',gap:8,justifyContent:'center',marginBottom:12}}>
        <StatCard label="Responded Avg Eff"        value={fmt(rAvgEff,1,'%')}  color={C_SEISMOS}  sub={`${responded.length} stages`}/>
        <StatCard label={neighborMode?'Neighbor Avg Eff (±3)':'Baseline Avg Eff'} value={fmt(bAvgEff,1,'%')} color={C_BASE} sub={neighborMode?'per-stage mean':'all other stages'}/>
        {delta!=null&&<StatCard label={neighborMode?'Δ Eff (neighbor-adj.)':'Δ Avg Eff'} value={fmtDelta(delta,1,'%')} color={dColor(delta)} sub={neighborMode?'bias-corrected':'vs all baseline'}/>}
        {rAvgUI!=null&&<StatCard label="Responded Avg UI" value={fmt(rAvgUI,3)} color={C_SEISMOS}/>}
        {bAvgUI!=null&&<StatCard label={neighborMode?'Neighbor Avg UI':'Baseline Avg UI'} value={fmt(bAvgUI,3)} color={C_BASE}/>}
        {neighborMode&&pctPos!=null&&<StatCard label="Outperformed Neighbors" value={`${pctPos}%`} color={pctPos>=50?'#7ecb8f':'#e05c5c'} sub="of responded stages"/>}
      </div>
      {responded.length<2
        ?<div style={{color:TEXT_DIM,fontSize:12,padding:'12px 0'}}>Need ≥ 2 logged Seismos-responded stages to draw comparison.</div>
        :<HighchartsReact highcharts={Highcharts} options={opts}/>}
      <div style={{marginTop:8,fontSize:10,color:TEXT_DIM,lineHeight:1.6}}>
        {mode==='all'
          ?'● Purple = responded · Gray = baseline · Dashed = linear trend · Switch to ±3 Neighbors for bias-corrected result'
          :'● Green dot = outperformed neighbors · Red dot = underperformed · ✕ = per-stage neighbor mean · Connector = Δ · ±3 controls toe/heel pressure accumulation (SPE-217824)'}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// VIEW 2 — Intra-Stage Event Study
// ══════════════════════════════════════════════════════════════════════════════
const ViewIntraStage:React.FC<{
  stages:StageMin[];respondedStageIds:Set<number>;
  seismosInterventions:SeismosInterventionRecord[];apiBase:string;token:string;
  level:'well'|'pad'; entityId:number;
}> = ({stages,respondedStageIds,seismosInterventions,apiBase,token,level,entityId}) => {
  const [loading,      setLoading]      = useState(false);
  const [loaded,       setLoaded]       = useState(false);
  const [error,        setError]        = useState<string|null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // All events for the pad/well — fetched once, split into IV vs baseline
  const [allEvents, setAllEvents] = useState<Array<{
    id:number; stage_id:number; stage_number:number; well_id:number; well_name:string;
    eff:number|null; event_time:string; event_time_ms:number|null;
  }>>([]);

  const ivByStage = useMemo(()=>{
    const m:Record<number,SeismosInterventionRecord>={};
    seismosInterventions.forEach(iv=>{m[iv.stage_id]=iv;});
    return m;
  },[seismosInterventions]);

  const taggedStages = useMemo(()=>
    stages.filter(s=>respondedStageIds.has(s.stage_id)&&ivByStage[s.stage_id]?.seismos_metrics_id!=null),
    [stages,respondedStageIds,ivByStage],
  );

  // Card visibility — defaults to none selected (chart always shows all)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Scalar baseline stats for summary cards
  const baselineStats = useMemo(()=>{
    const bl=stages.filter(s=>!respondedStageIds.has(s.stage_id));
    const effs=bl.map(s=>s.avg_eff).filter((v):v is number=>v!=null);
    if(!effs.length) return null;
    return{mean:effs.reduce((a,b)=>a+b)/effs.length, n:effs.length};
  },[stages,respondedStageIds]);

  // Single fetch: all seismos events for the pad/well
  const fetchAll = useCallback(async()=>{
    if(loaded) return;
    // Guard: props must be defined before fetching
    if(!level || !entityId) return;
    setLoading(true); setError(null);
    try {
      const res = await fetch(
        `${apiBase}/seismos/events/${level}/${entityId}`,
        {headers:{Authorization:`Bearer ${token}`}},
      );
      if(!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setAllEvents(data.events ?? []);
      setLoaded(true);
    } catch(e:any){setError((e as any).message??'Failed to load');}
    finally{setLoading(false);}
  },[level,entityId,apiBase,token,loaded]);

  useEffect(()=>{fetchAll();},[fetchAll]);

  // Close dropdown on outside click
  useEffect(()=>{
    const h=(e:MouseEvent)=>{if(dropdownRef.current&&!dropdownRef.current.contains(e.target as Node))setDropdownOpen(false);};
    document.addEventListener('mousedown',h);
    return()=>document.removeEventListener('mousedown',h);
  },[]);

  const study = useMemo(()=>{
    if(!loaded||!allEvents.length) return null;
    const WIN=4;

    // Group events by stage_id, sorted by event_time
    const byStage:Record<number,typeof allEvents> = {};
    allEvents.forEach(ev=>{
      if(!byStage[ev.stage_id]) byStage[ev.stage_id]=[];
      byStage[ev.stage_id].push(ev);
    });
    Object.values(byStage).forEach(evts=>evts.sort((a,b)=>(a.event_time_ms??0)-(b.event_time_ms??0)));

    // ── Intervention stages: center at tagged event ──
    const stageStudies:{
      stage_id:number;label:string;wellShort:string;stageNum:number;
      preEvents:{pos:number;eff:number}[];postEvents:{pos:number;eff:number}[];
      preAvg:number|null;postAvg:number|null;delta:number|null;
      preSlope:number|null;postSlope:number|null;
    }[] = [];

    taggedStages.forEach(s=>{
      const iv=ivByStage[s.stage_id];
      if(!iv?.seismos_metrics_id) return;
      const events=byStage[s.stage_id]??[];
      if(!events.length) return;
      const ivIdx=events.findIndex(e=>e.id===iv.seismos_metrics_id);
      if(ivIdx===-1) return;
      const pre:{pos:number;eff:number}[]=[], post:{pos:number;eff:number}[]=[];
      for(let i=Math.max(0,ivIdx-WIN);i<ivIdx;i++){const eff=toPercent(events[i].eff);if(eff!=null)pre.push({pos:i-ivIdx,eff});}
      for(let i=ivIdx;i<=Math.min(events.length-1,ivIdx+WIN);i++){const eff=toPercent(events[i].eff);if(eff!=null)post.push({pos:i-ivIdx,eff});}
      const preAvg=avg(pre.map(e=>e.eff)), postAvg=avg(post.map(e=>e.eff));
      const preReg=linearRegression(pre.map(e=>({x:e.pos,y:e.eff})));
      const postReg=linearRegression(post.map(e=>({x:e.pos,y:e.eff})));
      stageStudies.push({
        stage_id:s.stage_id, label:`${shortWell(s.well_name)} S${s.stage_number}`,
        wellShort:shortWell(s.well_name), stageNum:s.stage_number,
        preEvents:pre, postEvents:post, preAvg, postAvg,
        delta:preAvg!=null&&postAvg!=null?postAvg-preAvg:null,
        preSlope:preReg?.slope??null, postSlope:postReg?.slope??null,
      });
    });

    // ── Baseline stages: center at midpoint, average across all ──
    const blPositionData:{pos:number;eff:number}[]=[];
    Object.entries(byStage).forEach(([sid,events])=>{
      if(respondedStageIds.has(Number(sid))) return;  // skip intervention stages
      if(events.length<2) return;
      const midIdx=Math.floor(events.length/2);
      for(let i=Math.max(0,midIdx-WIN);i<=Math.min(events.length-1,midIdx+WIN);i++){
        const eff=toPercent(events[i].eff);
        if(eff!=null) blPositionData.push({pos:i-midIdx,eff});
      }
    });
    const xs=[-4,-3,-2,-1,0,1,2,3,4];
    const blMeans=xs.map(x=>{
      const ys=blPositionData.filter(p=>p.pos===x).map(p=>p.eff);
      return ys.length>=2?{x,y:ys.reduce((a,b)=>a+b)/ys.length,n:ys.length,stage_count:new Set(blPositionData.filter(p=>p.pos===x).map((_,i)=>i)).size}:null;
    }).filter((v):v is{x:number;y:number;n:number;stage_count:number}=>v!=null);
    const blMeanSlope=linearRegression(blMeans.map(p=>({x:p.x,y:p.y})))?.slope??null;
    const blStageCount=Object.keys(byStage).filter(sid=>!respondedStageIds.has(Number(sid))).length;

    const deltas=stageStudies.map(s=>s.delta).filter((v):v is number=>v!=null);
    const postSlopes=stageStudies.map(s=>s.postSlope).filter((v):v is number=>v!=null);
    const avgPostSlope=avg(postSlopes);
    const slopeDiff=avgPostSlope!=null&&blMeanSlope!=null?avgPostSlope-blMeanSlope:null;

    return{stageStudies,avgDelta:avg(deltas),avgPostSlope,blMeans,blMeanSlope,slopeDiff,blStageCount};
  },[loaded,allEvents,taggedStages,ivByStage,respondedStageIds]);

  const chartOpts = useMemo(():Highcharts.Options|null=>{
    if(!study||!study.stageStudies.length) return null;
    // Chart always shows ALL tagged stages (selectedIds only controls cards)
    const allPre:{x:number;y:number;label:string}[]=[], allPost:{x:number;y:number;label:string}[]=[];
    study.stageStudies.forEach(s=>{
      s.preEvents.forEach(e=>allPre.push({x:e.pos,y:e.eff,label:s.label}));
      s.postEvents.forEach(e=>allPost.push({x:e.pos,y:e.eff,label:s.label}));
    });
    const xs=[-4,-3,-2,-1,0,1,2,3,4];
    const preMeans=xs.filter(x=>x<0).map(x=>{const ys=allPre.filter(p=>p.x===x).map(p=>p.y);return ys.length?{x,y:ys.reduce((a,b)=>a+b)/ys.length}:null}).filter((v):v is{x:number;y:number}=>v!=null);
    const postMeans=xs.filter(x=>x>=0).map(x=>{const ys=allPost.filter(p=>p.x===x).map(p=>p.y);return ys.length?{x,y:ys.reduce((a,b)=>a+b)/ys.length}:null}).filter((v):v is{x:number;y:number}=>v!=null);
    const {blMeans,blMeanSlope,avgPostSlope}=study;
    const slopeParts=[
      avgPostSlope!=null?`Post-IV slope: ${fmtDelta(avgPostSlope,2,'%/event')}`:'',
      blMeanSlope!=null?`Baseline slope: ${fmtDelta(blMeanSlope,2,'%/event')}`:'',
      study.avgDelta!=null?`Avg Δ post−pre: ${fmtDelta(study.avgDelta,1,'%')}`:'',
    ].filter(Boolean).join('   ·   ');
    return{
      credits:{enabled:false},title:{text:undefined},
      subtitle:{text:slopeParts,style:{color:TEXT_SEC,fontSize:'10px'}},
      chart:{backgroundColor:BG,style:{fontFamily:"'DM Mono',monospace"},animation:false,height:310},
      legend:{itemStyle:{color:TEXT_SEC,fontSize:'11px',fontWeight:'normal'},itemHoverStyle:{color:TEXT_PRI},backgroundColor:'transparent'},
      xAxis:{lineColor:BORDER,tickColor:BORDER,gridLineWidth:0,labels:{style:{color:TEXT_SEC,fontSize:'10px'}},
        title:{text:'Events relative to intervention (0 = intervention event)',style:{color:TEXT_SEC,fontSize:'11px'}},
        plotLines:[{value:0,color:C_SEISMOS,width:1.5,zIndex:5,label:{text:'⬆ Intervention',style:{color:C_SEISMOS,fontSize:'9px'},y:14}}],
        plotBands:[{from:-0.5,to:0.5,color:`${C_SEISMOS}18`,zIndex:0}]},
      yAxis:{title:{text:'Perf Efficiency (%)',style:{color:TEXT_SEC,fontSize:'11px'}},
        labels:{format:'{value}%',style:{color:TEXT_SEC,fontSize:'10px'}},
        min:0,max:105,gridLineColor:BORDER,gridLineDashStyle:'Dot',lineWidth:0},
      tooltip:{backgroundColor:'#1e1c1b',borderColor:BORDER,style:{color:TEXT_PRI,fontSize:'12px'},useHTML:true,
        formatter:function(){
          const pt=this.point as any;
          if(this.series.name==='Baseline Avg')return `<span style="color:${C_BASE}">— Baseline avg at position ${pt.x}</span><br/>Eff: <b>${(pt.y as number).toFixed(1)}%</b><br/><span style="color:${TEXT_DIM};font-size:9px">n=${pt.n} events</span>`;
          return `<b>${pt.label}</b><br/>Event: ${(pt.x as number)>0?'+':''}${pt.x}<br/>Eff: <b>${(pt.y as number).toFixed(1)}%</b>`;
        }},
      series:[
        ...(blMeans.length>=2?[
          {type:'scatter' as const,name:'Baseline Events',color:`${C_BASE}66`,marker:{radius:5,symbol:'diamond' as const,fillColor:`${C_BASE}44`,lineWidth:1,lineColor:C_BASE},data:blMeans.map(p=>({x:p.x,y:p.y,n:p.n})),zIndex:2,showInLegend:true},
          {type:'line' as const,name:'Baseline Avg',color:C_BASE,dashStyle:'ShortDash' as const,lineWidth:2,marker:{enabled:false},data:blMeans.map(p=>[p.x,p.y]),enableMouseTracking:false,zIndex:2,showInLegend:true},
        ]:[]),
        {type:'scatter',name:'Pre-Intervention',color:C_PRE,marker:{radius:4,symbol:'circle',fillColor:`${C_PRE}88`},data:allPre.map(p=>({x:p.x,y:p.y,label:p.label})),zIndex:3},
        {type:'scatter',name:'Post-Intervention',color:C_POST,marker:{radius:4,symbol:'circle',fillColor:`${C_POST}88`},data:allPost.map(p=>({x:p.x,y:p.y,label:p.label})),zIndex:3},
        ...(preMeans.length>=2?[{type:'line' as const,name:'Pre Mean',color:C_PRE,dashStyle:'ShortDash' as const,lineWidth:2.5,marker:{enabled:false},data:preMeans.map(p=>[p.x,p.y]),enableMouseTracking:false,zIndex:4}]:[]),
        ...(postMeans.length>=2?[{type:'line' as const,name:'Post Mean',color:C_POST,dashStyle:'ShortDash' as const,lineWidth:2.5,marker:{enabled:false},data:postMeans.map(p=>[p.x,p.y]),enableMouseTracking:false,zIndex:4}]:[]),
      ] as Highcharts.SeriesOptionsType[],
    };
  },[study]);

  if(taggedStages.length===0) return(
    <div style={{color:TEXT_DIM,fontSize:12,padding:'24px 0',textAlign:'center'}}>
      No stages have a tagged intervention linked to a specific Seismos event.<br/>
      <span style={{fontSize:10,marginTop:4,display:'block'}}>Stage view → Event Log → click a row → Tag.</span>
    </div>
  );
  if(loading) return <div style={{color:TEXT_DIM,fontSize:12,padding:'20px 0',textAlign:'center'}}>Loading event data…</div>;
  if(error)   return <div style={{color:'#e05c5c',fontSize:12,padding:'12px 0'}}>⚠ {error}</div>;
  if(!study||!study.stageStudies.length) return <div style={{color:TEXT_DIM,fontSize:12,padding:'20px 0'}}>No event data available.</div>;

  const visibleCards = study.stageStudies.filter(s=>selectedIds.has(s.stage_id));

  return(
    <div>
      {/* Summary stat cards — fixed set, no pct improvement */}
      <div style={{display:'flex',flexWrap:'wrap',gap:8,justifyContent:'center',marginBottom:10}}>
        <StatCard label="Stages Analyzed"        value={`${study.stageStudies.length}`}            color={TEXT_PRI} sub={`of ${taggedStages.length} tagged`}/>
        <StatCard label="Avg Δ Eff (post−pre)"   value={fmtDelta(study.avgDelta,1,'%')}             color={dColor(study.avgDelta)} sub="within same stage"/>
        <StatCard label="Post-IV Eff Slope"       value={fmtDelta(study.avgPostSlope,2,'%/ev')}      color={dColor(study.avgPostSlope)} sub="avg across stages"/>
        <StatCard label="Baseline Eff Slope"      value={fmtDelta(study.blMeanSlope,2,'%/ev')}       color={dColor(study.blMeanSlope)} sub={`${study.blStageCount} baseline stages`}/>
        {/* Key metric: post-IV slope minus baseline slope (positive = slowed the decline) */}
        <StatCard label="Δ Slope (Post-IV−Base)"  value={fmtDelta(study.slopeDiff,2,'%/ev')}         color={dColor(study.slopeDiff)} sub="positive = decline slowed"/>
      </div>

      {/* Multi-select for stage cards — always visible */}
      <div ref={dropdownRef} style={{position:'relative',marginBottom:14}}>
        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
          <button onClick={()=>setDropdownOpen(o=>!o)} style={{
            background:CARD_BG, border:`1px solid ${dropdownOpen?C_SEISMOS:BORDER}`,
            color:TEXT_PRI, borderRadius:4, padding:'5px 12px', fontSize:10,
            fontWeight:600, cursor:'pointer', fontFamily:'inherit',
            display:'flex', alignItems:'center', gap:6,
          }}>
            <span style={{color:C_SEISMOS}}>●</span>
            {selectedIds.size===0?'Stage cards hidden':
             selectedIds.size===taggedStages.length?'All stage cards shown':
             `${selectedIds.size} of ${taggedStages.length} stage cards shown`}
            <span style={{color:TEXT_DIM,marginLeft:2}}>{dropdownOpen?'▲':'▼'}</span>
          </button>
          <button onClick={()=>setSelectedIds(new Set(taggedStages.map(s=>s.stage_id)))}
            style={{fontSize:9,color:TEXT_SEC,background:'transparent',border:'none',cursor:'pointer',padding:'2px 6px',fontFamily:'inherit'}}>All</button>
          <button onClick={()=>setSelectedIds(new Set())}
            style={{fontSize:9,color:TEXT_SEC,background:'transparent',border:'none',cursor:'pointer',padding:'2px 6px',fontFamily:'inherit'}}>None</button>
          {selectedIds.size===0&&(
            <span style={{fontSize:9,color:TEXT_DIM}}>— Chart shows all {taggedStages.length} stages · click to reveal cards</span>
          )}
        </div>

        {/* Dropdown panel — always usable */}
        {dropdownOpen&&(
          <div style={{
            position:'absolute',top:'100%',left:0,zIndex:20,
            background:CARD_BG, border:`1px solid ${C_SEISMOS}66`,
            borderRadius:6, padding:'6px 0', minWidth:230,
            boxShadow:'0 4px 16px rgba(0,0,0,0.5)',
          }}>
            {taggedStages.length===0&&(
              <div style={{padding:'8px 14px',color:TEXT_DIM,fontSize:11}}>No tagged stages yet</div>
            )}
            {taggedStages.map(s=>{
              const isChecked=selectedIds.has(s.stage_id);
              const ss=study?.stageStudies.find(st=>st.stage_id===s.stage_id);
              return(
                <label key={s.stage_id} style={{
                  display:'flex',alignItems:'center',gap:8,padding:'5px 14px',
                  cursor:'pointer',background:isChecked?`${C_SEISMOS}0a`:'transparent',
                }}>
                  <input type="checkbox" checked={isChecked} onChange={()=>setSelectedIds(ids=>{
                    const next=new Set(ids);
                    if(next.has(s.stage_id))next.delete(s.stage_id);else next.add(s.stage_id);
                    return next;
                  })} style={{accentColor:C_SEISMOS,width:12,height:12}}/>
                  <span style={{fontSize:11,color:TEXT_PRI,flex:1}}>{shortWell(s.well_name)} · S{s.stage_number}</span>
                  {ss?.delta!=null&&<span style={{fontSize:10,color:dColor(ss.delta),fontWeight:700}}>{fmtDelta(ss.delta,1,'%')}</span>}
                </label>
              );
            })}
          </div>
        )}

        {/* Per-stage cards — only visible stages */}
        {visibleCards.length>0&&(
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(118px, 1fr))',gap:6}}>
            {visibleCards.map(s=>(
              <div key={s.stage_id}
                onClick={()=>setSelectedIds(ids=>{const n=new Set(ids);if(n.has(s.stage_id))n.delete(s.stage_id);else n.add(s.stage_id);return n;})}
                style={{background:CARD_BG,border:`1px solid ${s.delta!=null&&s.delta>0?'#7ecb8f44':s.delta!=null&&s.delta<0?'#e05c5c44':BORDER}`,borderRadius:6,padding:'7px 10px',cursor:'pointer'}}
                title="Click to hide card"
              >
                <div style={{fontSize:9,color:TEXT_DIM,letterSpacing:'0.05em',textTransform:'uppercase',marginBottom:2}}>{s.wellShort} · S{s.stageNum}</div>
                <div style={{fontSize:14,fontWeight:700,color:dColor(s.delta),lineHeight:1.1}}>{fmtDelta(s.delta,1,'%')}</div>
                {s.preAvg!=null&&s.postAvg!=null&&<div style={{fontSize:9,color:TEXT_SEC,marginTop:2,fontVariantNumeric:'tabular-nums'}}>{s.preAvg.toFixed(1)}% → {s.postAvg.toFixed(1)}%</div>}
                {s.postSlope!=null&&<div style={{fontSize:9,color:dColor(s.postSlope),marginTop:1}}>slope {fmtDelta(s.postSlope,2,'%/ev')}</div>}
              </div>
            ))}
          </div>
        )}
      </div>

      {chartOpts&&<HighchartsReact highcharts={Highcharts} options={chartOpts}/>}
      <div style={{marginTop:8,fontSize:10,color:TEXT_DIM,lineHeight:1.6}}>
        Amber = pre-intervention · Purple = post-intervention · Gray ◆ = baseline avg eff at each event position (all non-IV stages, centered at stage midpoint) ·
        Δ Slope: positive means the post-IV eff decline rate is slower than baseline
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// VIEW 3 — Multi-Metric Δ Table (mirrors Excel Summary tab)
// ══════════════════════════════════════════════════════════════════════════════
const ViewMetrics:React.FC<{stages:StageMin[];respondedStageIds:Set<number>}> = ({stages,respondedStageIds}) => {
  const rows = useMemo(()=>{
    const responded=stages.filter(s=>respondedStageIds.has(s.stage_id));
    const baseline= stages.filter(s=>!respondedStageIds.has(s.stage_id));
    const nb=responded.map(s=>getNeighbors(s,stages,respondedStageIds,3));
    const metrics=[
      {label:'Uniformity Index',   unit:'',    dec:3, rV:responded.map(s=>s.uniformity_index),bV:baseline.map(s=>s.uniformity_index),nV:nb.map(n=>n.mean_ui)},
      {label:'Avg Perf Efficiency',unit:'%',   dec:1, rV:responded.map(s=>s.avg_eff),         bV:baseline.map(s=>s.avg_eff),         nV:nb.map(n=>n.mean_eff)},
      {label:'Avg EHD',            unit:' in', dec:3, rV:responded.map(s=>s.avg_ehd),          bV:baseline.map(s=>s.avg_ehd),         nV:nb.map(n=>n.mean_ehd)},
      {label:'Avg Perf Friction',  unit:' psi',dec:0, rV:responded.map(s=>s.avg_perf_frict),   bV:baseline.map(s=>s.avg_perf_frict),  nV:nb.map(n=>n.mean_pf)},
      {label:'Avg Pipe Friction',  unit:' psi',dec:0, rV:responded.map(s=>s.avg_pipe_frict),   bV:baseline.map(s=>s.avg_pipe_frict),  nV:nb.map(n=>n.mean_ppf)},
    ];
    return metrics.map(m=>{
      const rM=avg(m.rV),bM=avg(m.bV),nM=avg(m.nV);
      const dBl=rM!=null&&bM!=null?rM-bM:null;
      const dNb=rM!=null&&nM!=null?rM-nM:null;
      return{...m,rM,bM,nM,dBl,dNb,pBl:dBl!=null&&bM!=null&&bM!==0?dBl/bM*100:null,pNb:dNb!=null&&nM!=null&&nM!==0?dNb/nM*100:null};
    });
  },[stages,respondedStageIds]);

  const th:React.CSSProperties={padding:'6px 12px',textAlign:'right',color:TEXT_SEC,fontSize:9,fontWeight:600,letterSpacing:'0.06em',textTransform:'uppercase',borderBottom:`1px solid ${BORDER}`,whiteSpace:'nowrap'};
  const td:React.CSSProperties={padding:'8px 12px',fontSize:11,textAlign:'right',fontVariantNumeric:'tabular-nums',color:TEXT_PRI};
  return(
    <div>
      <div style={{fontSize:10,color:TEXT_SEC,marginBottom:10,letterSpacing:'0.03em'}}>
        Baseline = avg of all non-responded stages · ±3 Neighbor = avg of nearest 3 non-responded stages per well (controls toe/heel bias)
      </div>
      <div style={{overflowX:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
          <thead>
            <tr>
              <th style={{...th,textAlign:'left'}}>Metric</th>
              <th style={th}>Responded Mean</th>
              <th style={th}>Baseline Mean</th>
              <th style={{...th,color:C_BASE}}>Δ vs Baseline</th>
              <th style={{...th,color:C_BASE}}>Δ%</th>
              <th style={th}>±3 Neighbor Mean</th>
              <th style={{...th,color:C_SEISMOS}}>Δ vs Neighbors</th>
              <th style={{...th,color:C_SEISMOS}}>Δ%</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r,i)=>(
              <tr key={r.label} style={{background:i%2===0?'transparent':`${CARD_BG}88`,borderBottom:`1px solid ${BORDER}20`}}>
                <td style={{...td,textAlign:'left',color:GOLD,fontWeight:600}}>{r.label}</td>
                <td style={{...td,color:C_SEISMOS,fontWeight:700}}>{fmt(r.rM,r.dec,r.unit)}</td>
                <td style={td}>{fmt(r.bM,r.dec,r.unit)}</td>
                <td style={{...td,color:dColor(r.dBl),fontWeight:r.dBl!=null?600:400}}>{fmtDelta(r.dBl,r.dec,r.unit)}</td>
                <td style={{...td,color:dColor(r.pBl)}}>{fmtDelta(r.pBl,1,'%')}</td>
                <td style={td}>{fmt(r.nM,r.dec,r.unit)}</td>
                <td style={{...td,color:dColor(r.dNb),fontWeight:r.dNb!=null?600:400}}>{fmtDelta(r.dNb,r.dec,r.unit)}</td>
                <td style={{...td,color:dColor(r.pNb)}}>{fmtDelta(r.pNb,1,'%')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{marginTop:8,fontSize:10,color:TEXT_DIM,lineHeight:1.6}}>
        Green = responded outperformed · Red = underperformed · ±3 Neighbor is the bias-corrected figure (SPE-217824) ·
        Note: for EHD higher = better; for frictions lower = better
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// ROOT
// ══════════════════════════════════════════════════════════════════════════════
export const SeismosComparisonChart:React.FC<Props> = ({stages,respondedStageIds,showWell,seismosInterventions,apiBase,token,level,entityId}) => {
  const [view,  setView]  = useState<ViewKey>('compare');
  const [cMode, setCMode] = useState<CompareMode>('all');

  if(respondedStageIds.size===0) return null;

  return(
    <div style={{marginTop:24,borderTop:`1px solid ${BORDER}`,paddingTop:20}}>
      {/* Header + tabs */}
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:14,flexWrap:'wrap'}}>
        <span style={{background:`${C_SEISMOS}18`,border:`1px solid ${C_SEISMOS}55`,color:C_SEISMOS,borderRadius:3,padding:'2px 8px',fontSize:10,fontWeight:700}}>SEISMOS</span>
        <span style={{fontSize:11,color:TEXT_SEC,letterSpacing:'0.06em',textTransform:'uppercase'}}>Intervention Analysis</span>
        <span style={{color:TEXT_DIM,fontSize:10}}>({respondedStageIds.size} responded · {stages.length-respondedStageIds.size} baseline)</span>
        <div style={{marginLeft:'auto',display:'flex',gap:6,flexWrap:'wrap'}}>
          <Tab label="Stage Comparison"  active={view==='compare'} onClick={()=>setView('compare')} color={GOLD}/>
          <Tab label="Intra-Stage Trend" active={view==='intra'}   onClick={()=>setView('intra')}   color={C_POST}/>
          <Tab label="Multi-Metric Δ"   active={view==='metrics'} onClick={()=>setView('metrics')} color={C_BASE}/>
        </div>
      </div>

      {/* Stage Comparison sub-toggle */}
      {view==='compare'&&(
        <div style={{display:'flex',gap:6,marginBottom:12,alignItems:'center'}}>
          <span style={{fontSize:10,color:TEXT_SEC,marginRight:4}}>Comparison:</span>
          <Tab label="All Stages"   active={cMode==='all'}      onClick={()=>setCMode('all')}      color={GOLD}/>
          <Tab label="±3 Neighbors" active={cMode==='neighbor'} onClick={()=>setCMode('neighbor')} color={GOLD}/>
        </div>
      )}

      {view==='compare' &&<ViewCompare    stages={stages} respondedStageIds={respondedStageIds} showWell={showWell} mode={cMode}/>}
      {view==='intra'   &&<ViewIntraStage stages={stages} respondedStageIds={respondedStageIds} seismosInterventions={seismosInterventions} apiBase={apiBase} token={token} level={level} entityId={entityId}/>}
      {view==='metrics' &&<ViewMetrics    stages={stages} respondedStageIds={respondedStageIds}/>}
    </div>
  );
};

export default SeismosComparisonChart;